<template>
  <div :class="[$options.name, { 'label-top': labelTop }]">
    <label>
      <slot name="label">
        {{ labelTop || label }}
      </slot>
    </label>
    <div
      :class="[{ 'is-error': error }]"
      class="container"
      :style="{ minHeight: `${height}px` }"
    >
      <textarea
        ref="textarea"
        v-bind="$attrs"
        :style="{ minHeight: `${height}px` }"
        :name="name"
        :disabled="disabled"
        :required="required"
        :value="localValue"
        class="input-control editor textarea"
        @invalid="onInvalid"
      />
    </div>
  </div>
</template>

<script>
// todo: async imports

import defaultsDeep from 'lodash/defaultsDeep';
import { stripTags } from '@/utils/transform-text';
import MediumEditor from './medium-editor';
import converter from './converter';

const EDITOR_DEFAULTS = {
  toolbar: {
    buttons: ['h3', 'bold', 'italic', 'anchor', 'removeFormat'],
  },
  paste: { cleanPastedHTML: true },
  placeholder: { hideOnClick: false },
};

export default {
  name: 'rich-editor',
  inheritAttrs: false,
  props: {
    name: String,
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    rows: {
      type: Number,
      default: 5,
    },
    disabled: Boolean,
    required: Boolean,
    focus: Boolean,
    label: String,
    labelTop: String,
    limit: {
      type: Number,
      default: 0,
    },
    error: Boolean,
    errorMessage: String,
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    localValue: {
      get() {
        return converter.makeHtml(this.value || '');
      },
      set(rawHtml) {
        const md = converter.makeMarkdown(rawHtml);

        this.$emit('change', md);
        this.$emit('input', md);
        if (!md) {
          // fix `<p><br></p>` for show placeholder
          this.$refs.textarea.innerHTML = '';
          this.editor.elements.forEach(el => {
            // eslint-disable-next-line no-param-reassign
            el.innerHTML = '';
          });
        }
      },
    },
    editorConfig() {
      return defaultsDeep(
        {
          placeholder: { text: this.placeholder },
        },
        this.config,
        EDITOR_DEFAULTS,
      );
    },
    valueLength() {
      return stripTags(this.localValue).trim().length;
    },
    isLimitOver() {
      if (!this.valueLength || !this.limit) return false;
      return this.valueLength > this.limit;
    },

    height() {
      return (this.rows + 1) * 24;
    },
  },
  watch: {
    editorConfig() {
      this.$nextTick(() => this.initEditor());
    },
    disabled: {
      immediate: true,
      handler(disabled) {
        this.$nextTick(() => {
          const method = disabled ? 'setAttribute' : 'removeAttribute';
          this.editor.elements.forEach(el => el[method]('disabled', true));
        });
      },
    },
    focus: {
      immediate: true,
      handler(focus) {
        if (focus) this.setFocus();
      },
    },
    localValue(value) {
      this.$emit('change', value);
      this.$emit('input', value);
    },
  },
  mounted() {
    this.initEditor();
  },
  beforeDestroy() {
    this.destroyEditor();
  },
  methods: {
    initEditor() {
      this.destroyEditor();
      this.editor = new MediumEditor(this.$refs.textarea, this.editorConfig);
      this.editor.subscribe('editableInput', (event, el) => {
        this.localValue = el.innerHTML;
      });
      this.editor.subscribe('editableKeyup', (...args) => this.$emit('keyup', ...args));
      this.editor.subscribe('editableKeydown', (...args) => this.$emit('keydown', ...args));
      this.editor.subscribe('focus', (...args) => {
        this.setFocus();
        this.$emit('focus', ...args);
      });
    },
    destroyEditor() {
      if (this.editor) this.editor.destroy();
    },

    onInvalid() {
      setTimeout(() => window.scrollBy(0, -80));
    },

    setFocus() {
      if (!this.editor) return;

      this.$nextTick(() => {
        const el = this.editor.elements[0];
        if (el) el.focus();
      });
    },
  },
};
</script>

<style src="medium-editor/dist/css/medium-editor.css"></style>
<style src="./theme.scss" lang="scss"></style>

<style lang="scss" scoped>

.rich-editor {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  &.label-top {
    flex-wrap: wrap;
  }
}

.container {
  border: 1px solid rgba(0, 0, 0, 0.42);
  border-radius: 3px;
  min-height: 240px;
  flex-grow: 1;
  max-width: 100%;
  display: flex;
}

.editor,
.textarea {
  height: 100%;
  width: 100%;
  outline: none;
  border-radius: 3px;
  color: black;

  &:focus {
    outline: none;
  }

  &.medium-editor-hidden {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    min-width: 100%;
    min-height: 100%;
    z-index: -1;
  }

  > * {
    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  /* fix broken bold-button */
  b,
  strong {
    font-weight: 700 !important;
  }

  a {
    text-decoration: none;
    color: #778eff;
  }
}
</style>
